import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '../../utils/api';
import SuccessModal from '../../components/modals/SuccessModal';
import ErrorModal from '../../components/modals/ErrorModal';

const PayPalSetupPage = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState('sandbox'); // 'sandbox' or 'live'
  const [config, setConfig] = useState({
    active_mode: 'sandbox', // Which mode is currently active for transactions
    // Sandbox credentials
    sandbox_client_id: '',
    sandbox_client_secret: '',
    sandbox_webhook_id: '',
    // Live credentials
    live_client_id: '',
    live_client_secret: '',
    live_webhook_id: '',
    // Shared settings
    currency: 'GBP',
    vat_rate: 20,
    plans_created: false,
    webhook_url: '',
    frontend_return_url: `${window.location.origin}/api/paypal/return`,
    frontend_cancel_url: `${window.location.origin}/api/paypal/cancel`
  });
  
  const [successModal, setSuccessModal] = useState({ show: false, message: '' });
  const [errorModal, setErrorModal] = useState({ show: false, message: '' });
  const [showSecrets, setShowSecrets] = useState({ sandbox: false, live: false });
  const [testing, setTesting] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const copyToClipboard = (text, fieldId) => {
    navigator.clipboard.writeText(text);
    setCopiedField(fieldId);
    setTimeout(() => setCopiedField(null), 2000);
  };

  useEffect(() => {
    fetchConfig();
  }, []);

  const fetchConfig = async () => {
    try {
      setLoading(true);
      const response = await axios.get(`${API}/super-admin/paypal-config`, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      if (response.data) {
        // Ensure frontend URLs have defaults if not set
        const configWithDefaults = {
          ...response.data,
          frontend_return_url: `${window.location.origin}/api/paypal/return`,
          frontend_cancel_url: `${window.location.origin}/api/paypal/cancel`
        };
        setConfig(configWithDefaults);
        // Set active tab based on active mode
        setActiveTab(response.data.active_mode || 'sandbox');
      }
    } catch (error) {
      if (error.response?.status !== 404) {
        console.error('Failed to fetch config:', error);
      }
      // If 404, it means no config exists yet - use defaults
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    
    try {
      const response = await axios.post(`${API}/super-admin/paypal-config`, config, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setSuccessModal({
        show: true,
        message: 'PayPal configuration saved successfully!'
      });
      
      // Refresh to get the saved config
      await fetchConfig();
    } catch (error) {
      setErrorModal({
        show: true,
        message: 'Failed to save configuration: ' + (error.response?.data?.detail || 'Unknown error')
      });
    } finally {
      setSaving(false);
    }
  };

  const handleInputChange = (field, value) => {
    setConfig(prev => ({ ...prev, [field]: value }));
  };

  const testPayPalConnection = async (mode) => {
    setTesting(true);
    try {
      const response = await axios.post(`${API}/super-admin/paypal/test-connection`, 
        { mode },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      
      if (response.data.success) {
        setSuccessModal({
          show: true,
          message: `PayPal ${mode.toUpperCase()} authentication successful!\n\nBase URL: ${response.data.base_url}`
        });
      } else {
        setErrorModal({
          show: true,
          message: 'Authentication failed: ' + (response.data.error || 'Unknown error')
        });
      }
    } catch (error) {
      setErrorModal({
        show: true,
        message: 'Test failed: ' + (error.response?.data?.detail || error.message)
      });
    } finally {
      setTesting(false);
    }
  };

  const setActiveMode = async (mode) => {
    // Check if credentials exist for this mode
    // Note: Saved credentials are returned masked (e.g., '****abcd') from the backend for security
    // So we check if the value exists AND is not empty, rather than checking for asterisks
    const clientId = mode === 'sandbox' ? config.sandbox_client_id : config.live_client_id;
    const clientSecret = mode === 'sandbox' ? config.sandbox_client_secret : config.live_client_secret;
    
    // Credentials are considered "set" if they exist and have some value (even if masked)
    const hasCredentials = clientId && clientId.trim() !== '' && clientSecret && clientSecret.trim() !== '';
    
    if (!hasCredentials) {
      setErrorModal({
        show: true,
        message: `Please save ${mode} credentials before activating ${mode} mode.`
      });
      return;
    }
    
    // Update local state immediately for UI feedback
    const updatedConfig = { ...config, active_mode: mode };
    setConfig(updatedConfig);
    
    // Auto-save the mode change to the backend
    try {
      setSaving(true);
      await axios.post(`${API}/super-admin/paypal-config`, updatedConfig, {
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
      });
      
      setSuccessModal({
        show: true,
        message: `Switched to ${mode.toUpperCase()} mode successfully!`
      });
      
      // Refresh to confirm the change
      await fetchConfig();
    } catch (error) {
      // Revert local state on error
      setConfig(config);
      setErrorModal({
        show: true,
        message: 'Failed to switch mode: ' + (error.response?.data?.detail || 'Unknown error')
      });
    } finally {
      setSaving(false);
    }
  };

  // Check if credentials are configured for each mode
  // Masked secrets (e.g., '****abcd') indicate credentials ARE saved in the database
  const hasSandboxCredentials = config.sandbox_client_id && config.sandbox_client_secret;
  const hasLiveCredentials = config.live_client_id && config.live_client_secret;
  const hasCurrentTabCredentials = activeTab === 'sandbox' 
    ? (config.sandbox_client_id && config.sandbox_client_secret)
    : (config.live_client_id && config.live_client_secret);

  if (loading) {
    return (
      <div className="flex items-center justify-center p-8">
        <div className="inline-block animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600"></div>
        <span className="ml-3 text-gray-600">Loading configuration...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-gray-900 via-gray-800 to-purple-900 rounded-xl p-6 text-white mb-8 shadow-xl">
        <div className="flex items-center space-x-3">
          <div className="w-10 h-10 bg-gradient-to-r from-purple-600 to-purple-500 rounded-lg flex items-center justify-center">
            <svg className="w-6 h-6" viewBox="0 0 24 24" fill="currentColor">
              <path d="M7.076 21.337H2.47a.641.641 0 0 1-.633-.74L4.944 3.72a.775.775 0 0 1 .766-.66h6.79c2.067 0 3.57.534 4.462 1.586.393.463.68.987.85 1.554.178.593.236 1.277.168 2.032v.003l-.003.015c-.02.12-.044.244-.07.372l-.009.043-.01.038c-.5 2.572-2.208 4.292-4.794 4.826-.533.11-1.11.165-1.716.165H9.153a.95.95 0 0 0-.938.803l-.009.052-1.024 6.487-.008.045-.009.044-.004.026H7.16l-.085.166z"/>
            </svg>
          </div>
          <div>
            <h1 className="text-3xl font-bold">PayPal Setup</h1>
            <p className="text-purple-200 text-sm">Configure PayPal integration for subscription billing</p>
          </div>
        </div>
      </div>

      {/* Active Mode Indicator */}
      <div className={`rounded-lg p-4 border-2 ${
        config.active_mode === 'live' 
          ? 'bg-green-50 border-green-400' 
          : 'bg-amber-50 border-amber-400'
      }`}>
        <div className="flex items-center justify-between">
          <div className="flex items-center">
            <div className={`w-3 h-3 rounded-full mr-3 ${
              config.active_mode === 'live' ? 'bg-green-500' : 'bg-amber-500'
            } animate-pulse`}></div>
            <div>
              <p className={`font-bold text-sm ${
                config.active_mode === 'live' ? 'text-green-900' : 'text-amber-900'
              }`}>
                Active Mode: {config.active_mode?.toUpperCase() || 'SANDBOX'}
              </p>
              <p className={`text-sm ${
                config.active_mode === 'live' ? 'text-green-700' : 'text-amber-700'
              }`}>
                {config.active_mode === 'live' 
                  ? 'Live transactions are enabled. Real payments will be processed.'
                  : 'Sandbox mode is active. Test transactions only.'}
              </p>
            </div>
          </div>
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600">Switch to:</span>
            <button
              type="button"
              onClick={() => setActiveMode(config.active_mode === 'sandbox' ? 'live' : 'sandbox')}
              disabled={saving}
              className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                config.active_mode === 'sandbox'
                  ? 'bg-green-600 text-white hover:bg-green-700'
                  : 'bg-amber-600 text-white hover:bg-amber-700'
              }`}
            >
              {config.active_mode === 'sandbox' ? 'Live Mode' : 'Sandbox Mode'}
            </button>
          </div>
        </div>
      </div>

      {/* Information Box */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
        <div className="flex items-start">
          <svg className="w-5 h-5 text-blue-600 mr-3 mt-0.5 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          <div className="text-sm text-blue-800">
            <p className="font-semibold mb-2">How to get PayPal API Credentials:</p>
            <ol className="list-decimal ml-4 space-y-1">
              <li>Login to PayPal Developer Dashboard: <a href="https://developer.paypal.com/dashboard" target="_blank" rel="noopener noreferrer" className="underline font-semibold">developer.paypal.com</a></li>
              <li>Navigate to <strong>My Apps & Credentials</strong></li>
              <li>Toggle between <strong>Sandbox</strong> and <strong>Live</strong> tabs in PayPal</li>
              <li>Create or select an app, then copy the <strong>Client ID</strong> and <strong>Secret</strong></li>
              <li>Enter both sets of credentials below - Sandbox for testing, Live for production</li>
            </ol>
          </div>
        </div>
      </div>

      {/* Configuration Form */}
      <form onSubmit={handleSave} className="bg-white rounded-xl shadow-sm border border-gray-100">
        {/* Tab Navigation */}
        <div className="border-b border-gray-200">
          <nav className="flex -mb-px">
            <button
              type="button"
              onClick={() => setActiveTab('sandbox')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'sandbox'
                  ? 'border-amber-500 text-amber-600 bg-amber-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                </svg>
                <span>Sandbox (Testing)</span>
                {config.sandbox_client_id && (
                  <span className="ml-2 w-2 h-2 bg-amber-500 rounded-full"></span>
                )}
              </div>
            </button>
            <button
              type="button"
              onClick={() => setActiveTab('live')}
              className={`px-6 py-4 text-sm font-medium border-b-2 transition-colors ${
                activeTab === 'live'
                  ? 'border-green-500 text-green-600 bg-green-50'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              <div className="flex items-center space-x-2">
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
                </svg>
                <span>Live (Production)</span>
                {config.live_client_id && (
                  <span className="ml-2 w-2 h-2 bg-green-500 rounded-full"></span>
                )}
              </div>
            </button>
          </nav>
        </div>

        <div className="p-6 space-y-6">
          {/* Tab Content - Sandbox */}
          {activeTab === 'sandbox' && (
            <div className="space-y-6">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-amber-800">
                  <strong>Sandbox credentials</strong> are for testing. Use PayPal sandbox accounts to simulate transactions without real money.
                </p>
              </div>

              {/* Sandbox Client ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sandbox Client ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={config.sandbox_client_id}
                  onChange={(e) => handleInputChange('sandbox_client_id', e.target.value)}
                  placeholder="e.g., AeB1234...xyz"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  data-testid="sandbox-client-id-input"
                />
              </div>

              {/* Sandbox Client Secret */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sandbox Client Secret <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showSecrets.sandbox ? "text" : "password"}
                    value={config.sandbox_client_secret}
                    onChange={(e) => handleInputChange('sandbox_client_secret', e.target.value)}
                    placeholder="Enter your sandbox secret key"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent pr-12"
                    data-testid="sandbox-client-secret-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecrets(prev => ({ ...prev, sandbox: !prev.sandbox }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showSecrets.sandbox ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Sandbox Webhook ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Sandbox Webhook ID <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={config.sandbox_webhook_id}
                  onChange={(e) => handleInputChange('sandbox_webhook_id', e.target.value)}
                  placeholder="e.g., WH-1234...xyz"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-amber-500 focus:border-transparent"
                  data-testid="sandbox-webhook-id-input"
                />
                <p className="text-xs text-gray-500 mt-1">Webhook ID from PayPal Sandbox for signature verification</p>
              </div>

              {/* Test Connection Button */}
              {config.sandbox_client_id && config.sandbox_client_secret && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => testPayPalConnection('sandbox')}
                    disabled={testing || saving}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      testing || saving
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-amber-600 text-white hover:bg-amber-700'
                    }`}
                    data-testid="test-sandbox-connection-btn"
                  >
                    {testing ? 'Testing...' : 'Test Sandbox Connection'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Tab Content - Live */}
          {activeTab === 'live' && (
            <div className="space-y-6">
              <div className="bg-green-50 border border-green-200 rounded-lg p-3 mb-4">
                <p className="text-sm text-green-800">
                  <strong>Live credentials</strong> are for production. Real payments will be processed when live mode is active.
                </p>
              </div>

              {/* Live Client ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Live Client ID <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={config.live_client_id}
                  onChange={(e) => handleInputChange('live_client_id', e.target.value)}
                  placeholder="e.g., AeB1234...xyz"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="live-client-id-input"
                />
              </div>

              {/* Live Client Secret */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Live Client Secret <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <input
                    type={showSecrets.live ? "text" : "password"}
                    value={config.live_client_secret}
                    onChange={(e) => handleInputChange('live_client_secret', e.target.value)}
                    placeholder="Enter your live secret key"
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent pr-12"
                    data-testid="live-client-secret-input"
                  />
                  <button
                    type="button"
                    onClick={() => setShowSecrets(prev => ({ ...prev, live: !prev.live }))}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                  >
                    {showSecrets.live ? (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.88 9.88l-3.29-3.29m7.532 7.532l3.29 3.29M3 3l3.59 3.59m0 0A9.953 9.953 0 0112 5c4.478 0 8.268 2.943 9.543 7a10.025 10.025 0 01-4.132 5.411m0 0L21 21" />
                      </svg>
                    ) : (
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              {/* Live Webhook ID */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Live Webhook ID <span className="text-gray-400">(Optional)</span>
                </label>
                <input
                  type="text"
                  value={config.live_webhook_id}
                  onChange={(e) => handleInputChange('live_webhook_id', e.target.value)}
                  placeholder="e.g., WH-1234...xyz"
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent"
                  data-testid="live-webhook-id-input"
                />
                <p className="text-xs text-gray-500 mt-1">Webhook ID from PayPal Live for signature verification</p>
              </div>

              {/* Test Connection Button */}
              {config.live_client_id && config.live_client_secret && (
                <div className="pt-2">
                  <button
                    type="button"
                    onClick={() => testPayPalConnection('live')}
                    disabled={testing || saving}
                    className={`px-4 py-2 rounded-lg font-medium text-sm transition-all ${
                      testing || saving
                        ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                        : 'bg-green-600 text-white hover:bg-green-700'
                    }`}
                    data-testid="test-live-connection-btn"
                  >
                    {testing ? 'Testing...' : 'Test Live Connection'}
                  </button>
                </div>
              )}
            </div>
          )}

          {/* Divider */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-4">Shared Settings</h3>
          </div>

          {/* Currency */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Currency <span className="text-red-500">*</span>
            </label>
            <select
              value={config.currency}
              onChange={(e) => handleInputChange('currency', e.target.value)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
              data-testid="currency-select"
            >
              <option value="GBP">GBP (£) - British Pound</option>
              <option value="USD">USD ($) - US Dollar</option>
              <option value="EUR">EUR (€) - Euro</option>
              <option value="CAD">CAD ($) - Canadian Dollar</option>
              <option value="AUD">AUD ($) - Australian Dollar</option>
            </select>
            <p className="text-xs text-gray-500 mt-1">Currency for subscription billing</p>
          </div>

          {/* VAT Rate */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              VAT Rate (%) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="0"
              max="100"
              value={config.vat_rate}
              onChange={(e) => handleInputChange('vat_rate', parseInt(e.target.value) || 0)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
              data-testid="vat-rate-input"
            />
            <p className="text-xs text-gray-500 mt-1">VAT percentage included in plan prices (UK default: 20%)</p>
          </div>

          {/* Upgrade Fee Timeout */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Upgrade Fee Timeout (Hours) <span className="text-red-500">*</span>
            </label>
            <input
              type="number"
              min="1"
              max="168"
              value={config.upgrade_fee_timeout_hours || 24}
              onChange={(e) => handleInputChange('upgrade_fee_timeout_hours', parseInt(e.target.value) || 24)}
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent"
              required
              data-testid="upgrade-fee-timeout-input"
            />
            <p className="text-xs text-gray-500 mt-1">
              Time window for users to complete an upgrade after paying the fee (1-168 hours). 
              If not completed, payment is automatically refunded. Recommended: 24 hours.
            </p>
          </div>

          {/* Webhook URL (Auto-generated) */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Webhook URL <span className="text-gray-400">(Auto-generated)</span>
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                value={`${window.location.origin}/api/webhooks/paypal`}
                readOnly
                className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600"
              />
              <button
                type="button"
                onClick={() => {
                  navigator.clipboard.writeText(`${window.location.origin}/api/webhooks/paypal`);
                  setSuccessModal({ show: true, message: 'Webhook URL copied to clipboard!' });
                }}
                className="px-4 py-3 bg-indigo-100 text-indigo-700 rounded-lg hover:bg-indigo-200 font-medium"
              >
                Copy
              </button>
            </div>
            <p className="text-xs text-gray-500 mt-1">Use this URL when setting up webhooks in PayPal Developer Dashboard (for both Sandbox and Live)</p>
          </div>

          {/* Frontend Return URL - Read Only */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Success Return URL
            </label>
            <input
              type="text"
              value={`${window.location.origin}/api/paypal/return`}
              readOnly
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
              data-testid="return-url-input"
            />
            <p className="text-xs text-gray-500 mt-1">PayPal redirects here after approval. Backend processes tokens and redirects to frontend.</p>
          </div>

          {/* Frontend Cancel URL - Read Only */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Cancel Return URL
            </label>
            <input
              type="text"
              value={`${window.location.origin}/api/paypal/cancel`}
              readOnly
              className="w-full px-4 py-3 border border-gray-300 rounded-lg bg-gray-50 text-gray-600 cursor-not-allowed"
              data-testid="cancel-url-input"
            />
            <p className="text-xs text-gray-500 mt-1">PayPal redirects here if user cancels. Backend redirects to frontend.</p>
          </div>
        </div>

        {/* Form Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-100 flex justify-between items-center">
          <div className="text-sm text-gray-600">
            <span className="text-red-500">*</span> Required fields
          </div>
          <button
            type="submit"
            disabled={saving}
            className={`px-6 py-3 rounded-lg font-semibold transition-all ${
              saving
                ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                : 'bg-gradient-to-r from-indigo-600 to-purple-600 text-white hover:from-indigo-700 hover:to-purple-700 shadow-lg hover:shadow-xl'
            }`}
            data-testid="save-config-btn"
          >
            {saving ? (
              <span className="flex items-center">
                <svg className="animate-spin -ml-1 mr-2 h-4 w-4 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                </svg>
                Saving...
              </span>
            ) : (
              'Save Configuration'
            )}
          </button>
        </div>
      </form>

      {/* Credentials Summary */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Credentials Summary</h3>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {/* Sandbox Status */}
          <div className={`p-4 rounded-lg border-2 ${
            config.sandbox_client_id ? 'bg-amber-50 border-amber-300' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-3 ${
                  config.sandbox_client_id ? 'bg-amber-500' : 'bg-gray-300'
                }`}></div>
                <span className="font-medium text-gray-900">Sandbox</span>
              </div>
              {config.active_mode === 'sandbox' && (
                <span className="px-2 py-1 bg-amber-200 text-amber-800 text-xs font-medium rounded">ACTIVE</span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {config.sandbox_client_id 
                ? `Client ID: ${config.sandbox_client_id.substring(0, 12)}...`
                : 'Not configured'}
            </p>
          </div>

          {/* Live Status */}
          <div className={`p-4 rounded-lg border-2 ${
            config.live_client_id ? 'bg-green-50 border-green-300' : 'bg-gray-50 border-gray-200'
          }`}>
            <div className="flex items-center justify-between">
              <div className="flex items-center">
                <div className={`w-3 h-3 rounded-full mr-3 ${
                  config.live_client_id ? 'bg-green-500' : 'bg-gray-300'
                }`}></div>
                <span className="font-medium text-gray-900">Live</span>
              </div>
              {config.active_mode === 'live' && (
                <span className="px-2 py-1 bg-green-200 text-green-800 text-xs font-medium rounded">ACTIVE</span>
              )}
            </div>
            <p className="text-sm text-gray-600 mt-2">
              {config.live_client_id 
                ? `Client ID: ${config.live_client_id.substring(0, 12)}...`
                : 'Not configured'}
            </p>
          </div>
        </div>
      </div>

      {/* Field Names Reference */}
      <div className="bg-gray-50 rounded-xl border border-gray-200 p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">Field Reference (for Developers)</h3>
        <div className="text-sm text-gray-600 font-mono bg-white p-4 rounded-lg border overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="border-b">
                <th className="text-left py-2 pr-4">Field Name</th>
                <th className="text-left py-2">Description</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              <tr><td className="py-2 pr-4 text-indigo-600">active_mode</td><td>Current active mode ('sandbox' or 'live')</td></tr>
              <tr><td className="py-2 pr-4 text-amber-600">sandbox_client_id</td><td>PayPal Sandbox Client ID</td></tr>
              <tr><td className="py-2 pr-4 text-amber-600">sandbox_client_secret</td><td>PayPal Sandbox Client Secret</td></tr>
              <tr><td className="py-2 pr-4 text-amber-600">sandbox_webhook_id</td><td>PayPal Sandbox Webhook ID</td></tr>
              <tr><td className="py-2 pr-4 text-green-600">live_client_id</td><td>PayPal Live Client ID</td></tr>
              <tr><td className="py-2 pr-4 text-green-600">live_client_secret</td><td>PayPal Live Client Secret</td></tr>
              <tr><td className="py-2 pr-4 text-green-600">live_webhook_id</td><td>PayPal Live Webhook ID</td></tr>
              <tr><td className="py-2 pr-4 text-gray-600">currency</td><td>Billing currency (GBP, USD, EUR, etc.)</td></tr>
              <tr><td className="py-2 pr-4 text-gray-600">vat_rate</td><td>VAT percentage included in prices (default: 20%)</td></tr>
            </tbody>
          </table>
        </div>
      </div>

      {/* Next Steps Section */}
      {(config.sandbox_client_id || config.live_client_id) && (
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 border-2 border-green-300 rounded-lg p-6">
          <div className="flex items-start">
            <svg className="w-6 h-6 text-green-600 mr-3 flex-shrink-0 mt-0.5" fill="currentColor" viewBox="0 0 20 20">
              <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
            </svg>
            <div>
              <p className="font-bold text-sm text-green-900 mb-2">Configuration Saved!</p>
              <p className="text-sm text-green-800 mb-3">Your PayPal credentials are saved. Next steps:</p>
              <ol className="list-decimal ml-4 space-y-2 text-sm text-green-800">
                <li>Go to <strong>Subscription Packages</strong> page</li>
                <li>Click <strong>"Create PayPal Plans"</strong> button to sync your packages with PayPal</li>
                <li>Configure webhook events in PayPal Developer Dashboard</li>
                <li>Test subscription flow in sandbox mode</li>
                <li>Switch to live mode when ready for production</li>
              </ol>
            </div>
          </div>
        </div>
      )}

      {/* PayPal Links & Sandbox Credentials Section */}
      <div className="bg-slate-800 border border-slate-700 rounded-lg p-6">
        <h3 className="text-lg font-semibold text-white mb-4 flex items-center">
          <svg className="w-5 h-5 mr-2 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.828 10.172a4 4 0 00-5.656 0l-4 4a4 4 0 105.656 5.656l1.102-1.101m-.758-4.899a4 4 0 005.656 0l4-4a4 4 0 00-5.656-5.656l-1.1 1.1" />
          </svg>
          PayPal Links & Sandbox Credentials
        </h3>
        
        <div className="space-y-6">
          {/* URLs Section */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wider">URLs</h4>
            
            <div className="grid gap-3">
              <div className="flex items-center justify-between bg-slate-700/50 rounded-lg px-4 py-3">
                <div>
                  <span className="text-xs text-slate-400 block">Developer URL</span>
                  <span className="text-sm text-white font-mono">https://developer.paypal.com/</span>
                </div>
                <div className="flex items-center gap-2">
                  {copiedField === 'dev-url' && <span className="text-xs text-green-400">Copied!</span>}
                  <button
                    onClick={() => copyToClipboard('https://developer.paypal.com/', 'dev-url')}
                    className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                    title="Copy"
                  >
                    <svg className="w-4 h-4 text-slate-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between bg-slate-700/50 rounded-lg px-4 py-3">
                <div>
                  <span className="text-xs text-slate-400 block">Live URL</span>
                  <span className="text-sm text-white font-mono">https://www.paypal.com/uk/home</span>
                </div>
                <div className="flex items-center gap-2">
                  {copiedField === 'live-url' && <span className="text-xs text-green-400">Copied!</span>}
                  <button
                    onClick={() => copyToClipboard('https://www.paypal.com/uk/home', 'live-url')}
                    className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                    title="Copy"
                  >
                    <svg className="w-4 h-4 text-slate-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
              
              <div className="flex items-center justify-between bg-slate-700/50 rounded-lg px-4 py-3">
                <div>
                  <span className="text-xs text-slate-400 block">Sandbox URL</span>
                  <span className="text-sm text-white font-mono">https://sandbox.paypal.com</span>
                </div>
                <div className="flex items-center gap-2">
                  {copiedField === 'sandbox-url' && <span className="text-xs text-green-400">Copied!</span>}
                  <button
                    onClick={() => copyToClipboard('https://sandbox.paypal.com', 'sandbox-url')}
                    className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                    title="Copy"
                  >
                    <svg className="w-4 h-4 text-slate-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                    </svg>
                  </button>
                </div>
              </div>
            </div>
          </div>
          
          {/* Sandbox Accounts Section */}
          <div className="space-y-3">
            <h4 className="text-sm font-medium text-slate-400 uppercase tracking-wider">Sandbox Test Accounts</h4>
            
            {/* Personal Account */}
            <div className="bg-slate-700/50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <span className="bg-blue-500/20 text-blue-400 text-xs font-semibold px-2 py-1 rounded">Personal</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-slate-400 block">Username</span>
                    <span className="text-sm text-white font-mono truncate block">sb-dpsa947719783@personal.example.com</span>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    {copiedField === 'personal-user' && <span className="text-xs text-green-400">Copied!</span>}
                    <button
                      onClick={() => copyToClipboard('sb-dpsa947719783@personal.example.com', 'personal-user')}
                      className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                      title="Copy"
                    >
                      <svg className="w-4 h-4 text-slate-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-slate-400 block">Password</span>
                    <span className="text-sm text-white font-mono">EA^oGA3T</span>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    {copiedField === 'personal-pass' && <span className="text-xs text-green-400">Copied!</span>}
                    <button
                      onClick={() => copyToClipboard('EA^oGA3T', 'personal-pass')}
                      className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                      title="Copy"
                    >
                      <svg className="w-4 h-4 text-slate-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
            
            {/* Business Account */}
            <div className="bg-slate-700/50 rounded-lg p-4">
              <div className="flex items-center mb-3">
                <span className="bg-amber-500/20 text-amber-400 text-xs font-semibold px-2 py-1 rounded">Business</span>
              </div>
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-slate-400 block">Username</span>
                    <span className="text-sm text-white font-mono truncate block">sb-x6axe47700025@business.example.com</span>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    {copiedField === 'business-user' && <span className="text-xs text-green-400">Copied!</span>}
                    <button
                      onClick={() => copyToClipboard('sb-x6axe47700025@business.example.com', 'business-user')}
                      className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                      title="Copy"
                    >
                      <svg className="w-4 h-4 text-slate-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
                <div className="flex items-center justify-between">
                  <div className="flex-1 min-w-0">
                    <span className="text-xs text-slate-400 block">Password</span>
                    <span className="text-sm text-white font-mono">o/+DcZ}0</span>
                  </div>
                  <div className="flex items-center gap-2 ml-2">
                    {copiedField === 'business-pass' && <span className="text-xs text-green-400">Copied!</span>}
                    <button
                      onClick={() => copyToClipboard('o/+DcZ}0', 'business-pass')}
                      className="p-2 hover:bg-slate-600 rounded-lg transition-colors"
                      title="Copy"
                    >
                      <svg className="w-4 h-4 text-slate-400 hover:text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Modals */}
      <SuccessModal
        isOpen={successModal.show}
        onClose={() => setSuccessModal({ show: false, message: '' })}
        title="Success"
        message={successModal.message}
      />

      <ErrorModal
        isOpen={errorModal.show}
        onClose={() => setErrorModal({ show: false, message: '' })}
        title="Error"
        message={errorModal.message}
      />
    </div>
  );
};

export default PayPalSetupPage;
